import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import Box from '@mui/material/Box/Box'

import { DashboardAnalyticsFilter } from 'services/dashboard-analytics/types'

import PriceChart from 'components/dashboard-analytics/Analytics/Charts/PriceChart'
import ProfitChart from 'components/dashboard-analytics/Analytics/Charts/ProfitChart'
import ProxyTrafficChart from 'components/dashboard-analytics/Analytics/Charts/ProxyTrafficChart'
import type { DashboardFilterRequest } from 'components/dashboard-analytics/Analytics/Filter'
import DashboardFilter from 'components/dashboard-analytics/Analytics/Filter'
import InfoCards from 'components/dashboard-analytics/Analytics/InfoCards'
import ProfitableCategoriesTable from 'components/dashboard-analytics/Analytics/Tables/ProfitableCategoriesTable'
import ProfitableEventsTable from 'components/dashboard-analytics/Analytics/Tables/ProfitableEventsTable'
import RecentSalesTable from 'components/dashboard-analytics/Analytics/Tables/RecentSalesTable'

export const AnalyticsTab = () => {
  const location = useLocation()
  const getInitialFilters = (): DashboardAnalyticsFilter[] => {
    try {
      const paginationLS = localStorage.getItem('pagination')
      if (!paginationLS) return []
      const parsed = JSON.parse(paginationLS)
      const filtersLS = parsed?.[location.pathname]?.filters || []
      if (!Array.isArray(filtersLS)) return []
      return filtersLS
        .filter(
          (filter): filter is DashboardAnalyticsFilter =>
            filter &&
            typeof filter === 'object' &&
            'column' in filter &&
            'operator' in filter &&
            'valueType' in filter &&
            'values' in filter
        )
        .map((filter) => ({
          column: filter?.column,
          operator: filter?.operator,
          valueType: filter?.valueType,
          values: filter?.values.filter((value: string) => value && value.trim() !== '') || [],
        }))
    } catch (error) {
      console.warn('Failed to parse filters from localStorage:', error)
      return []
    }
  }

  const [filters, setFilters] = useState<DashboardAnalyticsFilter[]>(() => getInitialFilters())
  const handleApplyFilters = (filters: DashboardFilterRequest) => {
    setFilters(filters.filters)
  }

  const handleClearFilters = () => {
    setFilters([])
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '4px',
        mt: '8px',
      }}
    >
      <Box sx={{ p: '16px', mt: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <DashboardFilter
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          filters={filters}
        />
        <InfoCards filters={filters} />
        <Box sx={{ mt: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              width: '100%',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
            }}
          >
            <RecentSalesTable filters={filters} />
            <PriceChart filters={filters} />
          </Box>
          <ProfitableCategoriesTable filters={filters} />
          <ProfitableEventsTable filters={filters} />
          <ProfitChart filters={filters} />
          <ProxyTrafficChart />
        </Box>
      </Box>
    </Box>
  )
}
